import React from 'react';

const ListingHeader = ({ 
  listing, 
  listingId, 
  onNavigateBack, 
  onShowStatusModal, 
  onAction, 
  actionLoading,
  onViewPerformance 
}) => {
  const getStatusBadge = (status) => {
    const baseClasses = "px-3 py-1.5 text-sm font-medium rounded-full border";
    
    if (status === 'sold') {
      return <span className={`${baseClasses} bg-gray-700 text-gray-300 border-gray-600`}>Sold</span>;
    }
    if (status === 'inactive') {
      return <span className={`${baseClasses} bg-gray-700 text-gray-300 border-gray-600`}>Inactive</span>;
    }
    return <span className={`${baseClasses} bg-green-900/30 text-green-400 border-green-800/50`}>Active</span>;
  };

  const getModerationBadge = (moderationStatus) => {
    const baseClasses = "px-3 py-1.5 text-sm font-medium rounded-full border";
    
    switch (moderationStatus) {
      case 'pending':
        return <span className={`${baseClasses} bg-yellow-900/30 text-yellow-400 border-yellow-800/50`}>Pending Review</span>;
      case 'rejected':
        return <span className={`${baseClasses} bg-red-900/30 text-red-400 border-red-800/50`}>Rejected</span>;
      case 'approved':
        return <span className={`${baseClasses} bg-green-900/30 text-green-400 border-green-800/50`}>Approved</span>;
      case 'flagged':
        return <span className={`${baseClasses} bg-[#ff660e]/20 text-[#ff660e] border-[#ff660e]/30`}>Flagged</span>;
      default:
        return <span className={`${baseClasses} bg-gray-700 text-gray-300 border-gray-600`}>Unknown</span>;
    }
  };

  return (
    <div className="mb-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          {/* Back Navigation */}
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={onNavigateBack}
              className="flex items-center gap-2 text-gray-400 hover:text-[#ff660e] transition-all duration-200 group"
            >
              <span className="transform group-hover:-translate-x-1 transition-transform">←</span>
              <span>Back to Listings</span>
            </button>
            
            {/* ID Badge */}
            <span className="px-3 py-1 bg-gray-700 text-gray-300 text-sm font-medium rounded-full border border-gray-600">
              ID: {listingId}
            </span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">{listing?.title}</h1>
          
          {/* Status Badges */}
          <div className="flex flex-wrap gap-2">
            {getStatusBadge(listing?.status)}
            {getModerationBadge(listing?.moderation_status)}
            {listing?.is_featured && (
              <span className="px-3 py-1.5 bg-purple-900/30 text-purple-400 text-sm font-medium rounded-full border border-purple-800/50 flex items-center gap-1">
                <span>⭐</span>
                <span>Featured</span>
              </span>
            )}
            {listing?.is_negotiable && (
              <span className="px-3 py-1.5 bg-blue-900/30 text-blue-400 text-sm font-medium rounded-full border border-blue-800/50">
                Negotiable
              </span>
            )}
            {listing?.is_boosted && (
              <span className="px-3 py-1.5 bg-[#ff660e]/20 text-[#ff660e] text-sm font-medium rounded-full border border-[#ff660e]/30 flex items-center gap-1">
                <span>🚀</span>
                <span>Boosted</span>
              </span>
            )}
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          {/* Status Change Button */}
          <button
            onClick={onShowStatusModal}
            disabled={actionLoading}
            className="px-5 py-2.5 rounded-xl font-medium border-2 transition-all duration-200 hover:shadow hover:scale-[1.02] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed group"
            style={{ 
              borderColor: '#ff660e',
              color: '#ff660e',
              backgroundColor: 'transparent'
            }}
          >
            <span className="group-hover:rotate-180 transition-transform duration-500">🔄</span>
            <span>Change Status</span>
          </button>

          {/* Performance Button */}
          <button
            onClick={onViewPerformance}
            disabled={actionLoading}
            className="px-5 py-2.5 rounded-xl font-medium border-2 transition-all duration-200 hover:shadow hover:scale-[1.02] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed group"
            style={{ 
              borderColor: '#ff660e',
              color: '#ff660e',
              backgroundColor: 'transparent'
            }}
          >
            <span className="group-hover:scale-110 transition-transform">📈</span>
            <span>View Performance</span>
          </button>

          {/* Feature/Unfeature */}
          {listing?.moderation_status === 'approved' && listing?.status === 'active' && (
            <>
              {listing?.is_featured ? (
                <button
                  onClick={() => onAction('unfeature')}
                  disabled={actionLoading}
                  className="px-5 py-2.5 rounded-xl font-medium text-white transition-all duration-200 hover:shadow-lg hover:scale-[1.02] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-[#ff660e] to-orange-500"
                >
                  <span className="group-hover:rotate-12 transition-transform">⭐</span>
                  <span>{actionLoading ? 'Unfeaturing...' : 'Unfeature'}</span>
                </button>
              ) : (
                <button
                  onClick={() => onAction('feature')}
                  disabled={actionLoading}
                  className="px-5 py-2.5 rounded-xl font-medium text-white transition-all duration-200 hover:shadow-lg hover:scale-[1.02] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-[#ff660e] to-orange-500"
                >
                  <span className="group-hover:rotate-12 transition-transform">⭐</span>
                  <span>{actionLoading ? 'Featuring...' : 'Feature'}</span>
                </button>
              )}
            </>
          )}

          {/* Delete Button */}
          <button
            onClick={() => onAction('delete')}
            disabled={actionLoading}
            className="px-5 py-2.5 rounded-xl font-medium text-white transition-all duration-200 hover:shadow-lg hover:scale-[1.02] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-red-600 to-red-700"
          >
            <span className="group-hover:scale-110 transition-transform">🗑️</span>
            <span>{actionLoading ? 'Deleting...' : 'Delete'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ListingHeader;